import { useEffect } from 'react';
import { create } from 'zustand';
import type { UserServerMessage } from '@indusketch/shared';
import { useAuthStore } from '../features/auth/authStore.js';
import { startRingAlert } from '../features/editor/call/ringAlert.js';
import { CONSENT_KEY, setPendingJoin } from '../features/editor/call/pendingJoin.js';
import { apiRequest } from '../lib/api/client.js';
import { saveOpenEditor } from '../lib/openEditor.js';
import { Icon } from '../ui/Icon.js';
import { currentI18n, useI18n } from '../i18n/i18n.js';

interface GlobalRing {
  readonly projectId: string;
  readonly projectName: string;
  readonly messageId: string;
  readonly by: string;
}

const useGlobalRing = create<{ ring: GlobalRing | null }>()(() => ({ ring: null }));

const RETRY_MS = [1000, 2000, 5000, 10_000];
/** Below the 60 s idle timeout of the proxies in front of the API. */
const PING_MS = 25_000;
/** A ring nobody cancels (server restarted) does not stay forever. */
const RING_HIDE_MS = 60_000;

let stopAlert: (() => void) | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

function clearRing(): void {
  stopAlert?.();
  stopAlert = null;
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = null;
  useGlobalRing.setState({ ring: null });
}

/** Whether the project is the one on screen: its own room rings there. */
const showing = (projectId: string) => window.location.pathname === `/projects/${projectId}`;

function onMessage(message: UserServerMessage): void {
  if (message.type === 'ring') {
    if (showing(message.projectId)) return;
    clearRing();
    const { projectId, projectName, messageId, by } = message;
    useGlobalRing.setState({ ring: { projectId, projectName, messageId, by } });
    const { m, fmt } = currentI18n();
    stopAlert = startRingAlert(
      fmt(m.call.incomingProject, { name: by, project: projectName }),
      m.call.notificationBody,
    );
    hideTimer = setTimeout(clearRing, RING_HIDE_MS);
  } else if (message.type === 'ring-cancel') {
    if (useGlobalRing.getState().ring?.messageId === message.messageId) clearRing();
  }
}

/** The personal channel: open while signed in, reconnecting after drops. */
function openChannel(): () => void {
  let ws: WebSocket | null = null;
  let stopped = false;
  let attempts = 0;
  let retry: ReturnType<typeof setTimeout> | null = null;
  const connect = () => {
    if (stopped) return;
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(`${scheme}://${location.host}/api/live/me`);
    ws = socket;
    socket.onopen = () => {
      attempts = 0;
    };
    socket.onmessage = (e) => {
      try {
        onMessage(JSON.parse(String(e.data)) as UserServerMessage);
      } catch (err) {
        console.error(err);
      }
    };
    socket.onclose = (e) => {
      if (ws === socket) ws = null;
      // Signed out or not allowed: no point in trying again.
      if (stopped || e.code === 4401 || e.code === 4403) return;
      retry = setTimeout(connect, RETRY_MS[Math.min(attempts++, RETRY_MS.length - 1)]);
    };
  };
  connect();
  const ping = setInterval(() => {
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
  }, PING_MS);
  return () => {
    stopped = true;
    if (retry) clearTimeout(retry);
    clearInterval(ping);
    ws?.close(1000, 'leave');
    clearRing();
  };
}

/**
 * Calls from projects one is not looking at: rings wherever in the app one is. Taking the
 * call saves the open project and switches to the calling one, which then joins the call.
 */
export function GlobalCalls({ navigate }: { navigate: (path: string) => void }) {
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  const ring = useGlobalRing((s) => s.ring);
  const { m, fmt } = useI18n();

  useEffect(() => (signedIn ? openChannel() : undefined), [signedIn]);

  if (!ring) return null;
  const t = m.call;
  const accept = async () => {
    clearRing();
    sessionStorage.setItem(CONSENT_KEY, '1');
    setPendingJoin(ring.projectId);
    await saveOpenEditor();
    navigate(`/projects/${ring.projectId}`);
  };
  const decline = () => {
    clearRing();
    apiRequest(
      'POST',
      `/api/projects/${encodeURIComponent(ring.projectId)}/call/decline`,
      null,
    ).catch(() => undefined);
  };
  return (
    <div
      className="incoming-call is-ringing is-global"
      role="alertdialog"
      aria-label={t.title}
      data-testid="global-incoming-call"
    >
      <span className="incoming-call-icon" aria-hidden>
        <Icon name="phone" size={20} />
      </span>
      <div className="incoming-call-text">
        <strong>{fmt(t.incomingProject, { name: ring.by, project: ring.projectName })}</strong>
        <span>
          {t.acceptSwitch} {t.acceptNote}
        </span>
      </div>
      <button
        type="button"
        className="btn btn-primary incoming-accept"
        onClick={() => void accept()}
        data-testid="global-call-accept"
      >
        <Icon name="phone" />
        {t.accept}
      </button>
      <button type="button" className="btn" onClick={decline} data-testid="global-call-decline">
        <Icon name="phone-off" />
        {t.decline}
      </button>
    </div>
  );
}
